import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { FiCheck, FiX } from "react-icons/fi";
import { RegisteredUser } from "../../types/generic";
import { useTickets } from "../../store/ticket-context";
import {
  PageHeading,
  FilterTabs,
  LoadingState,
  ErrorState,
  EmptyState,
} from "../../components/UI";
import TicketItem from "../../components/Tickets/Item";
import styles from "./styles.module.css";
export default function UserStats({ user }: { user: RegisteredUser }) {
  const { tickets, loading, failedRobots, pending, updateStatus, reload } =
    useTickets();
  const [filter, setFilter] = useState<"All" | "Open" | "Closed">("All");
  const location = useLocation();
  const serialNumber = new URLSearchParams(location.search).get("robot");
  const available = serialNumber
    ? tickets.filter((entry) => entry.serialNumber === serialNumber)
    : tickets;
  const open = available.filter((entry) => entry.ticket.status !== "Closed");
  const visible =
    filter === "All"
      ? available
      : available.filter(
          (entry) =>
            (entry.ticket.status === "Closed") === (filter === "Closed"),
        );
  const cleanParams = new URLSearchParams(location.search);
  cleanParams.delete("robot");
  return (
    <>
      <PageHeading
        title="Service tickets"
        count={
          !loading
            ? `${available.length} ticket${available.length === 1 ? "" : "s"}`
            : undefined
        }
      />
      <FilterTabs
        label="Filter tickets by status"
        value={filter}
        onChange={setFilter}
        options={[
          { value: "All", label: "All", count: available.length },
          { value: "Open", label: "Open", count: open.length },
          {
            value: "Closed",
            label: "Closed",
            count: available.length - open.length,
          },
        ]}
      />
      {serialNumber && (
        <div className={styles.robotFilter}>
          <span>
            {user.serialNumbers?.[serialNumber]?.name || serialNumber}
          </span>
          <Link
            to={{ pathname: location.pathname, search: cleanParams.toString() }}
            aria-label="Show tickets for all robots"
          >
            <FiX aria-hidden="true" /> All robots
          </Link>
        </div>
      )}
      {!!failedRobots.length && (
        <ErrorState
          message="Some tickets could not be loaded."
          onRetry={reload}
        />
      )}
      {loading ? (
        <LoadingState label="Loading tickets…" />
      ) : visible.length ? (
        <div className={styles.table}>
          <div className={styles.tableHeading} aria-hidden="true">
            <span>Issue</span>
            <span>Robot</span>
            <span>Created</span>
            <span>Status</span>
            <span />
          </div>
          {visible.map((entry) => (
            <TicketItem
              key={`${entry.serialNumber}:${entry.id}`}
              ticket={entry.ticket}
              robotName={entry.robotName}
              serialNumber={entry.serialNumber}
              pending={pending.includes(`${entry.serialNumber}:${entry.id}`)}
              onUpdateStatus={(status) => updateStatus(entry, status)}
            />
          ))}
        </div>
      ) : (
        !failedRobots.length && (
          <EmptyState
            title={
              filter === "Closed"
                ? "No closed tickets"
                : filter === "Open"
                  ? "No open tickets"
                  : "No tickets yet"
            }
            description={
              filter === "Open"
                ? "Your robots have no unresolved fault reports."
                : "Tickets are created from automatic fault reports."
            }
            artwork={
              <div className={styles.check}>
                <FiCheck size={28} aria-hidden="true" />
              </div>
            }
          />
        )
      )}
      {!!visible.length && (
        <p className={styles.note}>
          Tickets are created from automatic fault reports.
        </p>
      )}
    </>
  );
}
